import { rpc, readCsrfToken } from "../api/rpc";
import type { Doc, UploadInstructions, User } from "../api/types";
import { fileDownload } from "../lib/csv";
import type { MentionSearchResult, MentionSearchScope, UploadResult } from "../editor/types";

/**
 * What the editing surface needs from the application: somewhere to put files,
 * somebody to ask about `@` mentions, and a way to turn a pasted link into a
 * document reference. The editor takes these as props, so this module is the
 * only place that knows both sides.
 */

/**
 * `docs/spec/attachments.md` §3.1: reserve the attachment, then send the bytes
 * to the URL the reservation names, with every form field it returned, in
 * order, and the file last.
 */
export async function uploadAttachment(file: File, documentId?: string): Promise<UploadResult> {
  const reservation = await rpc<UploadInstructions>("attachments.create", {
    name: file.name,
    contentType: file.type === "" ? "application/octet-stream" : file.type,
    size: file.size,
    ...(documentId === undefined ? {} : { documentId }),
  });
  const instructions = reservation.data;

  if (instructions.mode === "put") {
    const response = await fetch(instructions.uploadUrl, {
      method: "PUT",
      headers: instructions.headers ?? {},
      body: file,
    });
    if (!response.ok) throw new Error("The file could not be uploaded");
    return { url: instructions.attachment.url, size: file.size };
  }

  const form = new FormData();
  for (const [name, value] of Object.entries(instructions.form ?? {})) {
    form.append(name, value);
  }
  form.append("file", file, file.name);

  const csrf = readCsrfToken();
  const response = await fetch(instructions.uploadUrl, {
    method: "POST",
    credentials: "include",
    ...(csrf === null ? {} : { headers: { "x-csrf-token": csrf } }),
    body: form,
  });
  if (!response.ok) throw new Error("The file could not be uploaded");
  return { url: instructions.attachment.url, size: file.size };
}

function documentResult(document: Doc): MentionSearchResult {
  return {
    modelId: document.id,
    type: "document",
    label: document.title === "" ? "Untitled" : document.title,
    href: document.url,
  };
}

function userResult(user: User): MentionSearchResult {
  return {
    modelId: user.id,
    type: "user",
    label: user.name,
    ...(user.email === undefined ? {} : { subtitle: user.email }),
  };
}

/**
 * `@` searches people and documents; `[[` is scoped to documents
 * (`docs/spec/editor.md` §2). Both halves run in parallel and either may fail
 * without taking the menu down with it.
 */
export async function searchMentions(
  query: string,
  scope: MentionSearchScope,
): Promise<MentionSearchResult[]> {
  const trimmed = query.trim();
  // `documents.search_titles` requires a non-blank query, so an empty menu asks
  // only for people rather than sending one it knows will be refused.
  const documents: Promise<MentionSearchResult[]> =
    trimmed === ""
      ? Promise.resolve([])
      : rpc<Doc[]>("documents.search_titles", { query: trimmed, limit: 10 })
          .then((result) => result.data.map(documentResult))
          .catch(() => []);

  if (scope === "document") return documents;

  const users = rpc<User[]>("users.list", {
    ...(trimmed === "" ? {} : { query: trimmed }),
    limit: 10,
  })
    .then((result) => result.data.map(userResult))
    .catch(() => []);

  const [people, docs] = await Promise.all([users, documents]);
  return [...people, ...docs];
}

/** A `[[` entry that names nothing yet creates the document it names. */
export async function createDocumentFromMention(
  title: string,
  context: { collectionId?: string | null; parentDocumentId?: string | null },
): Promise<MentionSearchResult | null> {
  const result = await rpc<Doc>("documents.create", {
    title,
    ...(context.collectionId == null ? {} : { collectionId: context.collectionId }),
    ...(context.parentDocumentId == null ? {} : { parentDocumentId: context.parentDocumentId }),
    ...(context.collectionId == null ? {} : { publish: true }),
  });
  return documentResult(result.data);
}

/**
 * Turns a pasted link into a document reference when it points at a document in
 * this workspace, and leaves it alone otherwise.
 */
export async function resolveDocumentUrl(url: string): Promise<MentionSearchResult | null> {
  const slug = documentSlugFromUrl(url);
  if (slug === null) return null;
  try {
    const result = await rpc<Doc>("documents.info", { id: slug });
    return documentResult(result.data);
  } catch {
    return null;
  }
}

/** `/doc/<slug>` or `/d/<slug>`, absolute or relative, else null. */
export function documentSlugFromUrl(url: string): string | null {
  let path = url;
  if (/^https?:\/\//u.test(url)) {
    try {
      const parsed = new URL(url);
      if (typeof window !== "undefined" && parsed.origin !== window.location.origin) return null;
      path = parsed.pathname;
    } catch {
      return null;
    }
  }
  const match = /^\/(?:doc|d)\/([^/?#]+)/u.exec(path);
  return match?.[1] ?? null;
}

/**
 * `documents.export` with `Accept: text/markdown` answers with the file itself
 * rather than an envelope, so it goes out through `fetch` rather than `rpc`.
 * The save is the shared download seam, which tests replace.
 */
export async function exportDocumentMarkdown(id: string, title: string): Promise<void> {
  const csrf = readCsrfToken();
  const response = await fetch("/api/documents.export", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      accept: "text/markdown",
      ...(csrf === null ? {} : { "x-csrf-token": csrf }),
    },
    body: JSON.stringify({ id }),
  });
  if (!response.ok) throw new Error("The document could not be exported");
  const markdown = await response.text();
  const name = `${title === "" ? "Untitled" : title}.md`;
  fileDownload.save(name, markdown, "text/markdown;charset=utf-8");
}
